/**
 * Ending the session — the other half of `password-login.ts`, and for the same
 * reason: the interesting part is not the POST, it is what has to happen
 * around it, and a handler wired to a DOM event cannot be proved.
 *
 * Sign-out used to be `clearSession()` plus a reload. That was a real sign-out
 * only while the console's credential lived in sessionStorage. It does not: a
 * browser sign-in is an HttpOnly cookie this code can neither read nor delete,
 * and goal 231 removed the last thing that ever wrote a bearer — so the clear
 * removes a key nothing put there, the cookie survives the reload, and the
 * operator lands straight back in the console. Only the server can end that
 * session, so this asks it to.
 */
import { clearSession } from "./session";

/**
 * Better Auth's sign-out endpoint, RELATIVE for the same reason `SIGN_IN_PATH`
 * is. The session cookie is host-only (`__Secure-better-auth.session_token`,
 * no `Domain=`), so it is only ever sent to — and only ever cleared on — the
 * host the browser is talking to. An absolute URL at the auth Worker would
 * carry no cookie, delete no session, and answer 200 anyway. The console
 * Worker forwards `/api/auth/*` over a service binding precisely so this can
 * stay relative (`worker/auth-proxy.ts`).
 */
export const SIGN_OUT_PATH = "/api/auth/sign-out";

interface Deps {
  fetchFn?: typeof fetch;
  /** Drop whatever bearer this tab still holds. Injected only for tests. */
  forgetStoredBearer?: () => void;
  /** Hand this tab to the sign-in screen. Injected only for tests. */
  handOver?: () => void;
  /** Where a failed sign-out request is reported. Injected only for tests. */
  reportFailure?: (error: unknown) => void;
}

/**
 * Sign out, then hand this tab over to the sign-in screen.
 *
 * The hand-over is a full load of `/`, which is how the 401 path reaches
 * sign-in as well: the gate re-runs `/api/admin/me`, that request now carries
 * no live credential, `lib/api.ts` turns the 401 into the sign-in phase
 * (`gate-phase.ts`). A router push would leave the console mounted with a
 * TanStack cache still full of the customer data the operator just signed out
 * of; the reload throws all of it away.
 *
 * A FAILED request changes none of that. The operator clicked Sign out — often
 * on a machine they are about to walk away from — so local state is dropped and
 * the hand-over happens whatever the network said. What a failure does change
 * is that the server-side session may still be alive, and nothing else on this
 * path would ever say so, hence the report.
 */
export async function signOut(deps: Deps = {}): Promise<void> {
  const fetchFn = deps.fetchFn ?? fetch;
  const forgetStoredBearer = deps.forgetStoredBearer ?? clearSession;
  const handOver = deps.handOver ?? (() => window.location.assign("/"));
  const reportFailure =
    deps.reportFailure ??
    ((error: unknown) => console.error("Sign-out request failed", error));

  try {
    const response = await fetchFn(SIGN_OUT_PATH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // The cookie IS the request. Same-origin already sends it by default;
      // saying so keeps that true rather than leaving it to a default.
      credentials: "same-origin",
      // Better Auth's sign-out body is optional and every field in it is
      // optional too, but the endpoint is declared with a JSON body schema —
      // an empty object is the shape it parses without complaint.
      body: "{}",
    });

    if (!response.ok) {
      reportFailure(new Error(`Sign-out responded ${response.status}.`));
    }
  } catch (error) {
    reportFailure(error);
  }

  forgetStoredBearer();
  handOver();
}
